import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import api from "../../services/api";

const STATUTS = {
  non_transmis: {
    label: "Non transmis",
    color: "#9e9e9e",
    bg: "#f5f5f5",
    icon: "radio_button_unchecked",
  },
  en_cours: {
    label: "En cours",
    color: "#1565c0",
    bg: "#e3f2fd",
    icon: "pending",
  },
  transmis: {
    label: "Transmis",
    color: "#e65100",
    bg: "#fff3e0",
    icon: "check_circle_outline",
  },
  valide: {
    label: "Validé",
    color: "#2e7d32",
    bg: "#e8f5e9",
    icon: "check_circle",
  },
};

const fieldStyle = {
  width: "100%", height: "36px", padding: "0 10px",
  borderRadius: "8px", border: "1px solid #d2d6da",
  fontSize: "13px", outline: "none", fontFamily: "inherit",
  boxSizing: "border-box",
};

const getTermeChapitre = (matiere, pluriel = false) => {
  if (matiere === "quoran") return pluriel ? "sourates" : "sourate";
  return pluriel ? "chapitres" : "chapitre";
};

function StatutBadge({ statut, onClick, size = "normal" }) {
  const s = STATUTS[statut] || STATUTS.non_transmis;
  return (
    <MDBox onClick={onClick} sx={{
      display: "inline-flex", alignItems: "center", gap: 0.5,
      padding: size === "small" ? "3px 10px" : "5px 14px",
      borderRadius: "20px", background: s.bg, color: s.color,
      fontSize: size === "small" ? 11 : 12, fontWeight: 600,
      cursor: onClick ? "pointer" : "default",
      border: `1px solid ${s.color}33`,
      transition: "all 0.2s",
      "&:hover": onClick ? { background: s.color, color: "#fff" } : {},
      userSelect: "none",
    }}>
      <Icon sx={{ fontSize: size === "small" ? 12 : 14 }}>{s.icon}</Icon>
      {s.label}
    </MDBox>
  );
}

function SuiviTimeline({ eleveId, coursId, enseignantDefaut, onTermineChange }) {
  const [livreTermine, setLivreTermine] = useState(false);
  const [toast, setToast] = useState(null);
  const [livres, setLivres] = useState([]);
  const [selectedLivre, setSelectedLivre] = useState(null);
  const [suiviData, setSuiviData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [enseignants, setEnseignants] = useState([]);
  const [editForm, setEditForm] = useState({
    statut: "", notes: "", date_debut: "", date_fin: "",
    note_examen: "", resultat_examen: "", enseignant_id: "",
  });

  const chargerSuiviPourLivre = (livre, eId) => {
    setSelectedLivre(livre);
    setLoading(true);
    api.get(`/suivis/eleve/${eId}/livre/${livre.id}${coursId ? `?cours_id=${coursId}` : ""}`)
      .then(({ data: sd }) => setSuiviData(sd))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  // Charger la liste des enseignants
  useEffect(() => {
    api.get(`/enseignants?t=${Date.now()}`)
      .then(({ data }) => setEnseignants(data))
      .catch((err) => console.error("Erreur enseignants:", err));
  }, []);

  useEffect(() => {
    if (!eleveId) return;

    setLivres([]);
    setSelectedLivre(null);
    setSuiviData([]);

    if (coursId) {
      api.get(`/cours/${coursId}`).then(({ data }) => {
        setLivreTermine(!!data.chapitre_termine);
        const livreIds = new Set();
        (data.chapitres || []).forEach((chap) => {
          if (chap.livre_id) livreIds.add(chap.livre_id);
        });
        if (livreIds.size === 0) { setLivres([]); return; }
        api.get("/livres").then(({ data: allLivres }) => {
          const livresEleve = allLivres.filter((l) => livreIds.has(l.id));
          setLivres(livresEleve);
          if (livresEleve.length > 0) {
            chargerSuiviPourLivre(livresEleve[0], eleveId);
          }
        });
      });
    } else {
      api.get("/cours?tous=true").then(({ data }) => {
        const coursEleve = data.filter((c) => c.eleve_id === parseInt(eleveId));
        const livreIds = new Set();
        coursEleve.forEach((cours) => {
          (cours.chapitres || []).forEach((chap) => {
            if (chap.livre_id) livreIds.add(chap.livre_id);
          });
        });
        if (livreIds.size === 0) { setLivres([]); return; }
        api.get("/livres").then(({ data: allLivres }) => {
          const livresEleve = allLivres.filter((l) => livreIds.has(l.id));
          setLivres(livresEleve);
          if (livresEleve.length > 0) {
            chargerSuiviPourLivre(livresEleve[0], eleveId);
          }
        });
      });
    }
  }, [eleveId, coursId]);

  const loadSuivi = async (livre) => {
    chargerSuiviPourLivre(livre, eleveId);
  };

  const openEdit = (item) => {
    setEditingItem(item.chapitre.id);
    setEditForm({
      statut: item.statut || "non_transmis",
      notes: item.notes || "",
      date_debut: item.date_debut || "",
      date_fin: item.date_fin || "",
      note_examen: item.note_examen || "",
      resultat_examen: item.resultat_examen || "",
      // Utiliser l'enseignant du suivi, sinon l'enseignant du cours par défaut
      enseignant_id: item.enseignant?.id || enseignantDefaut?.id || "",
    });
  };

  const saveItem = async (item) => {
    setSaving(true);
    try {
      await api.post("/suivis", {
        eleve_id: eleveId,
        chapitre_id: item.chapitre.id,
        livre_id: selectedLivre.id,
        cours_id: coursId,
        ...editForm,
      });
      setSuiviData((prev) =>
        prev.map((s) => {
          if (s.chapitre.id === item.chapitre.id) {
            const enseignant = enseignants.find((e) => e.id === parseInt(editForm.enseignant_id));
            return { ...s, ...editForm, enseignant: enseignant || s.enseignant };
          }
          return s;
        })
      );
      setEditingItem(null);
    } catch (error) {
      console.error("Erreur:", error);
    } finally {
      setSaving(false);
    }
  };

  const updateStatut = async (item, newStatut) => {
    setSaving(true);
    try {
      await api.post("/suivis", {
        eleve_id: eleveId,
        chapitre_id: item.chapitre.id,
        livre_id: selectedLivre.id,
        cours_id: coursId,
        statut: newStatut,
        notes: item.notes,
        date_debut: item.date_debut,
        date_fin: item.date_fin,
        note_examen: item.note_examen,
        resultat_examen: item.resultat_examen,
        enseignant_id: item.enseignant?.id || enseignantDefaut?.id || "",
      });
      setSuiviData((prev) =>
        prev.map((s) =>
          s.chapitre.id === item.chapitre.id ? { ...s, statut: newStatut } : s
        )
      );
    } catch (error) {
      console.error("Erreur:", error);
    } finally {
      setSaving(false);
    }
  };

  const nextStatut = (current) => {
    const order = ["non_transmis", "en_cours", "transmis", "valide"];
    const idx = order.indexOf(current);
    return order[(idx + 1) % order.length];
  };

  const toggleLivreTermine = async (val) => {
    if (val === livreTermine) return;
    try {
      await api.put(`/cours/${coursId}`, { chapitre_termine: val });
      setLivreTermine(val);
      if (onTermineChange) onTermineChange(val);
      setToast({ severity: "success", msg: val ? "Livre marqué comme terminé" : "Livre marqué comme non terminé" });
    } catch (err) {
      setToast({ severity: "error", msg: err.response?.data?.message || "Erreur lors de la mise à jour" });
    }
  };

  const progression = suiviData.length > 0
    ? Math.round((suiviData.filter((s) => s.statut === "valide").length / suiviData.length) * 100)
    : 0;

  const stats = Object.keys(STATUTS).map((key) => ({
    key,
    count: suiviData.filter((s) => s.statut === key).length,
  }));

  const termePluriel = getTermeChapitre(selectedLivre?.matiere, true);
  const termeSingulier = getTermeChapitre(selectedLivre?.matiere, false);
  const prefixe = selectedLivre?.matiere === "quoran" ? "Sour." : "Chap.";

  // Livre terminé (uniquement dans le contexte d'un cours)
  const toggleTermine = coursId ? (
    <MDBox mb={3} sx={{
      display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
      padding: { xs: "16px", sm: "18px 24px" }, borderRadius: "12px",
      background: livreTermine ? "#f1f8e9" : "#fff",
      border: `1.5px solid ${livreTermine ? "#4caf50" : "#e0e0e0"}`,
      transition: "all 0.2s ease",
    }}>
      <Icon sx={{ fontSize: "28px !important", color: livreTermine ? "#4caf50" : "#9e9e9e" }}>
        {livreTermine ? "check_circle" : "hourglass_empty"}
      </Icon>
      <MDBox flex={1} minWidth={160}>
        <MDTypography sx={{
          fontSize: 16, fontWeight: 600, lineHeight: 1.3,
          color: livreTermine ? "#2e7d32" : "#616161",
        }}>
          {livreTermine ? "Livre terminé" : "Livre en cours"}
        </MDTypography>
        <MDTypography sx={{ fontSize: 13, color: livreTermine ? "#66bb6a" : "#9e9e9e" }}>
          {livreTermine ? "Cliquez pour rouvrir" : "Marquer comme terminé"}
        </MDTypography>
      </MDBox>
      {livreTermine ? (
        <MDButton variant="outlined" size="small"
          onClick={() => toggleLivreTermine(false)}
          sx={{
            borderRadius: "8px", padding: "8px 20px",
            borderColor: "#e57373", color: "#e57373",
            "&:hover": { borderColor: "#e57373", background: "#ffebee" },
          }}>
          Rouvrir
        </MDButton>
      ) : (
        <MDButton variant="outlined" size="small"
          onClick={() => toggleLivreTermine(true)}
          sx={{
            borderRadius: "8px", padding: "8px 20px",
            borderColor: "#4caf50", color: "#2e7d32",
            "&:hover": { borderColor: "#4caf50", background: "#f1f8e9" },
          }}>
          Terminer ce cours
        </MDButton>
      )}
    </MDBox>
  ) : null;

  if (livres.length === 0) return (
    <MDBox>
      {toggleTermine}
      <MDBox display="flex" flexDirection="column" alignItems="center" py={3} gap={1}
        sx={{ background: "#f8faff", borderRadius: "10px" }}>
        <Icon sx={{ fontSize: 40, color: "#e0e0e0" }}>menu_book</Icon>
        <MDTypography variant="body2" color="text">Aucun livre disponible</MDTypography>
      </MDBox>
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.msg}</Alert> : <span />}
      </Snackbar>
    </MDBox>
  );

  return (
    <MDBox>
      {/* Sélecteur de livre */}
      <MDBox display="flex" gap={1} flexWrap="wrap" mb={3}>
        {livres.map((livre) => (
          <MDBox key={livre.id} onClick={() => loadSuivi(livre)} sx={{
            padding: "6px 16px", borderRadius: "20px", cursor: "pointer",
            border: "1px solid",
            borderColor: selectedLivre?.id === livre.id ? "#1565c0" : "#d2d6da",
            background: selectedLivre?.id === livre.id ? "#1565c0" : "#fff",
            color: selectedLivre?.id === livre.id ? "#fff" : "#344767",
            fontSize: 13, fontWeight: 500, transition: "all 0.2s",
          }}>
            {livre.nom}
          </MDBox>
        ))}
      </MDBox>

      {selectedLivre && (
        <>
          {/* Barre de progression */}
          {suiviData.length > 0 && (
            <MDBox mb={3}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <MDTypography variant="caption" fontWeight="bold" sx={{
                  textTransform: "uppercase", fontSize: 11,
                  letterSpacing: "0.05em", color: "#9e9e9e",
                }}>
                  Progression globale
                </MDTypography>
                <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#2e7d32" }}>
                  {progression}% validé
                </MDTypography>
              </MDBox>
              <MDBox sx={{ background: "#eeeeee", borderRadius: "4px", height: 8, overflow: "hidden" }}>
                <MDBox sx={{
                  width: `${progression}%`, height: "100%",
                  background: "linear-gradient(90deg, #43a047, #66bb6a)",
                  borderRadius: "4px", transition: "width 0.5s ease",
                }} />
              </MDBox>
              <MDBox display="flex" gap={1} mt={1.5} flexWrap="wrap">
                {stats.map(({ key, count }) => count > 0 && (
                  <StatutBadge key={key} statut={key} size="small" />
                ))}
                <MDTypography variant="caption" color="text" sx={{ ml: "auto" }}>
                  {suiviData.filter(s => s.statut === "valide").length} / {suiviData.length} {termePluriel} validé{suiviData.filter(s => s.statut === "valide").length > 1 ? "s" : ""}
                </MDTypography>
              </MDBox>
            </MDBox>
          )}

          {toggleTermine}

          {loading ? (
            <MDBox display="flex" justifyContent="center" py={3}>
              <MDTypography variant="body2" color="text">Chargement...</MDTypography>
            </MDBox>
          ) : suiviData.length === 0 ? (
            <MDBox display="flex" flexDirection="column" alignItems="center" py={3} gap={1}
              sx={{ background: "#f8faff", borderRadius: "10px" }}>
              <Icon sx={{ fontSize: 40, color: "#e0e0e0" }}>format_list_numbered</Icon>
              <MDTypography variant="body2" color="text">
                Aucun{selectedLivre?.matiere === "quoran" ? "ne" : ""} {termeSingulier} dans ce livre
              </MDTypography>
            </MDBox>
          ) : (
            <MDBox>
              {suiviData.map((item, index) => {
                const s = STATUTS[item.statut] || STATUTS.non_transmis;
                const isLast = index === suiviData.length - 1;
                const isEditing = editingItem === item.chapitre.id;

                return (
                  <MDBox key={item.chapitre.id} display="flex" gap={2}>
                    <MDBox display="flex" flexDirection="column" alignItems="center" sx={{ minWidth: 32 }}>
                      <MDBox sx={{
                        width: 32, height: 32, borderRadius: "50%",
                        background: s.bg, border: `2px solid ${s.color}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0, zIndex: 1,
                      }}>
                        <Icon sx={{ fontSize: 16, color: s.color }}>{s.icon}</Icon>
                      </MDBox>
                      {!isLast && (
                        <MDBox sx={{
                          width: 2, flex: 1, minHeight: 20,
                          background: item.statut === "valide" ? "#2e7d32" : "#e0e0e0",
                          my: 0.5,
                        }} />
                      )}
                    </MDBox>

                    <MDBox flex={1} pb={isLast ? 0 : 2}>
                      <MDBox sx={{
                        background: isEditing ? s.bg : "#f8f8f8",
                        borderRadius: "12px", padding: "12px 16px",
                        border: `1px solid ${isEditing ? s.color + "44" : "#f0f0f0"}`,
                        transition: "all 0.2s",
                      }}>
                        <MDBox display="flex" alignItems="flex-start"
                          justifyContent="space-between" flexWrap="wrap" gap={1} mb={isEditing ? 2 : 0}>
                          <MDBox flex={1}>
                            <MDTypography variant="button" fontWeight="bold" sx={{ color: "#344767" }}>
                              {selectedLivre?.matiere === "quoran" ? item.chapitre.titre : `${prefixe} ${item.chapitre.numero} — ${item.chapitre.titre}`}
                            </MDTypography>

                            {!isEditing && (
                              <MDBox display="flex" flexWrap="wrap" gap={1} mt={1}>
                                {item.enseignant && (
                                  <MDBox sx={{
                                    display: "inline-flex", alignItems: "center", gap: 0.5,
                                    padding: "3px 10px", borderRadius: "8px",
                                    background: "#f3e5f5", color: "#6a1b9a",
                                    fontSize: 11, fontWeight: 700,
                                  }}>
                                    <Icon sx={{ fontSize: 12 }}>person</Icon>
                                    {item.enseignant.prenom} {item.enseignant.nom}
                                  </MDBox>
                                )}
                                {item.date_debut && (
                                  <MDBox sx={{
                                    display: "inline-flex", alignItems: "center", gap: 0.5,
                                    padding: "3px 10px", borderRadius: "8px",
                                    background: "#e3f2fd", color: "#1565c0",
                                    fontSize: 11, fontWeight: 600,
                                  }}>
                                    <Icon sx={{ fontSize: 12 }}>calendar_today</Icon>
                                    Début : {new Date(item.date_debut).toLocaleDateString("fr-FR")}
                                  </MDBox>
                                )}
                                {item.date_fin && (
                                  <MDBox sx={{
                                    display: "inline-flex", alignItems: "center", gap: 0.5,
                                    padding: "3px 10px", borderRadius: "8px",
                                    background: "#f3e5f5", color: "#6a1b9a",
                                    fontSize: 11, fontWeight: 600,
                                  }}>
                                    <Icon sx={{ fontSize: 12 }}>event</Icon>
                                    Fin : {new Date(item.date_fin).toLocaleDateString("fr-FR")}
                                  </MDBox>
                                )}
                                {item.note_examen && (
                                  <MDBox sx={{
                                    display: "inline-flex", alignItems: "center", gap: 0.5,
                                    padding: "3px 10px", borderRadius: "8px",
                                    background: item.resultat_examen === "reussi" ? "#e8f5e9"
                                      : item.resultat_examen === "echec" ? "#ffebee" : "#fff3e0",
                                    color: item.resultat_examen === "reussi" ? "#2e7d32"
                                      : item.resultat_examen === "echec" ? "#c62828" : "#e65100",
                                    fontSize: 11, fontWeight: 600,
                                  }}>
                                    <Icon sx={{ fontSize: 12 }}>school</Icon>
                                    Examen : {item.note_examen}
                                    {item.resultat_examen === "reussi" ? " ✅"
                                      : item.resultat_examen === "echec" ? " ❌" : " ⏳"}
                                  </MDBox>
                                )}
                              </MDBox>
                            )}

                            {item.date_mise_a_jour && !isEditing && (
                              <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                                Mis à jour le {new Date(item.date_mise_a_jour).toLocaleDateString("fr-FR")}
                              </MDTypography>
                            )}
                          </MDBox>

                          <MDBox display="flex" alignItems="center" gap={1} flexShrink={0}>
                            <StatutBadge statut={item.statut}
                              onClick={() => updateStatut(item, nextStatut(item.statut))} />
                            <MDBox
                              onClick={() => isEditing ? setEditingItem(null) : openEdit(item)}
                              sx={{
                                width: 28, height: 28, borderRadius: "8px",
                                background: isEditing ? "#ffebee" : "#e3f2fd",
                                color: isEditing ? "#c62828" : "#1565c0",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                cursor: "pointer",
                              }}>
                              <Icon sx={{ fontSize: 14 }}>{isEditing ? "close" : "edit"}</Icon>
                            </MDBox>
                          </MDBox>
                        </MDBox>

                        {isEditing && (
                          <MDBox>
                            <Grid container spacing={1.5}>
                              <Grid item xs={12}>
                                <MDTypography variant="caption" fontWeight="bold" sx={{
                                  textTransform: "uppercase", fontSize: 10,
                                  letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 0.5,
                                }}>
                                  Statut
                                </MDTypography>
                                <MDBox display="flex" gap={1} flexWrap="wrap">
                                  {Object.entries(STATUTS).map(([key, st]) => (
                                    <MDBox key={key}
                                      onClick={() => setEditForm((p) => ({ ...p, statut: key }))}
                                      sx={{
                                        padding: "4px 12px", borderRadius: "20px", cursor: "pointer",
                                        border: "1px solid",
                                        borderColor: editForm.statut === key ? st.color : "#d2d6da",
                                        background: editForm.statut === key ? st.color : "#fff",
                                        color: editForm.statut === key ? "#fff" : "#344767",
                                        fontSize: 12, fontWeight: 500, transition: "all 0.2s",
                                      }}>
                                      {st.label}
                                    </MDBox>
                                  ))}
                                </MDBox>
                              </Grid>

                              {/* Sélecteur enseignant */}
                              <Grid item xs={12}>
                                <MDTypography variant="caption" fontWeight="bold" sx={{
                                  textTransform: "uppercase", fontSize: 10,
                                  letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 0.5,
                                }}>
                                  Enseignant de ce chapitre
                                </MDTypography>
                                <MDBox display="flex" gap={1} flexWrap="wrap">
                                  {enseignants.map((ens) => (
                                    <MDBox key={ens.id}
                                      onClick={() => setEditForm((p) => ({ ...p, enseignant_id: ens.id }))}
                                      sx={{
                                        padding: "4px 12px", borderRadius: "20px", cursor: "pointer",
                                        border: "1px solid",
                                        borderColor: parseInt(editForm.enseignant_id) === ens.id ? "#6a1b9a" : "#d2d6da",
                                        background: parseInt(editForm.enseignant_id) === ens.id ? "#6a1b9a" : "#fff",
                                        color: parseInt(editForm.enseignant_id) === ens.id ? "#fff" : "#344767",
                                        fontSize: 12, fontWeight: 500, transition: "all 0.2s",
                                      }}>
                                      {ens.prenom} {ens.nom}
                                    </MDBox>
                                  ))}
                                </MDBox>
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <MDTypography variant="caption" fontWeight="bold" sx={{
                                  textTransform: "uppercase", fontSize: 10,
                                  letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 0.5,
                                }}>
                                  Date de début
                                </MDTypography>
                                <input type="date" value={editForm.date_debut || ""}
                                  onChange={(e) => setEditForm((p) => ({ ...p, date_debut: e.target.value }))}
                                  style={fieldStyle} />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <MDTypography variant="caption" fontWeight="bold" sx={{
                                  textTransform: "uppercase", fontSize: 10,
                                  letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 0.5,
                                }}>
                                  Date de fin
                                </MDTypography>
                                <input type="date" value={editForm.date_fin || ""}
                                  onChange={(e) => setEditForm((p) => ({ ...p, date_fin: e.target.value }))}
                                  style={fieldStyle} />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <MDTypography variant="caption" fontWeight="bold" sx={{
                                  textTransform: "uppercase", fontSize: 10,
                                  letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 0.5,
                                }}>
                                  Note d&apos;examen
                                </MDTypography>
                                <input type="text" value={editForm.note_examen || ""}
                                  onChange={(e) => setEditForm((p) => ({ ...p, note_examen: e.target.value }))}
                                  placeholder="Ex: 15/20, Bien..." style={fieldStyle} />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <MDTypography variant="caption" fontWeight="bold" sx={{
                                  textTransform: "uppercase", fontSize: 10,
                                  letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 0.5,
                                }}>
                                  Résultat
                                </MDTypography>
                                <select value={editForm.resultat_examen || ""}
                                  onChange={(e) => setEditForm((p) => ({ ...p, resultat_examen: e.target.value }))}
                                  style={{ ...fieldStyle, color: editForm.resultat_examen ? "#344767" : "#9ca3af" }}>
                                  <option value="">— Choisir —</option>
                                  <option value="reussi">✅ Réussi</option>
                                  <option value="echec">❌ Échoué</option>
                                  <option value="en_attente">⏳ En attente</option>
                                </select>
                              </Grid>
                              <Grid item xs={12}>
                                <MDTypography variant="caption" fontWeight="bold" sx={{
                                  textTransform: "uppercase", fontSize: 10,
                                  letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 0.5,
                                }}>
                                  Notes / Observations
                                </MDTypography>
                                <textarea value={editForm.notes || ""}
                                  onChange={(e) => setEditForm((p) => ({ ...p, notes: e.target.value }))}
                                  placeholder="Observations..."
                                  rows={2}
                                  style={{ ...fieldStyle, height: "auto", padding: "8px 10px", resize: "vertical" }} />
                              </Grid>
                            </Grid>
                            <MDBox display="flex" gap={1} mt={2} justifyContent="flex-end">
                              <MDBox onClick={() => setEditingItem(null)} sx={{
                                padding: "6px 16px", borderRadius: "8px", cursor: "pointer",
                                background: "#f5f5f5", color: "#757575",
                                fontSize: 12, fontWeight: 600,
                              }}>
                                Annuler
                              </MDBox>
                              <MDBox onClick={() => saveItem(item)} sx={{
                                padding: "6px 16px", borderRadius: "8px", cursor: "pointer",
                                background: saving ? "#ccc" : "#1565c0",
                                color: "#fff", fontSize: 12, fontWeight: 600,
                              }}>
                                {saving ? "Enregistrement..." : "Sauvegarder"}
                              </MDBox>
                            </MDBox>
                          </MDBox>
                        )}

                        {item.notes && !isEditing && (
                          <MDBox mt={1} px={1.5} py={0.8}
                            sx={{ background: "#fffde7", borderRadius: "8px", borderLeft: "3px solid #f9a825" }}>
                            <MDTypography variant="caption" sx={{ color: "#5d4037" }}>
                              📝 {item.notes}
                            </MDTypography>
                          </MDBox>
                        )}
                      </MDBox>
                    </MDBox>
                  </MDBox>
                );
              })}
            </MDBox>
          )}
        </>
      )}
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.msg}</Alert> : <span />}
      </Snackbar>
    </MDBox>
  );
}

export default SuiviTimeline;